import { RedeemCodeFailureReason } from "~/graphql/generated/graphql"

/**
 * What each failure means to the customer, mirroring myLifeway's own copy
 * (`mylifeway-frontend/utils/redeemMessages.js`).
 *
 * Wording, not decoration: the server's `message` is operator-facing and leaks
 * commerce vocabulary ("distribution", "LIN"), so it is logged rather than
 * rendered. Each sentence here says what happened and what to do next, because
 * three of these six are recoverable by the customer and three are not.
 *
 * Typed as a total `Record`, so a reason added to `schema.poc.gql` fails
 * `type-check` here rather than rendering blank to a customer.
 */
const MESSAGES: Record<RedeemCodeFailureReason, string> = {
  [RedeemCodeFailureReason.Invalid]: "We don't recognize that code. Check it for typos and try again.",
  [RedeemCodeFailureReason.Expired]: "That code has expired and can no longer be redeemed.",
  [RedeemCodeFailureReason.NotYetActive]: "That code isn't active yet. Check the start date on your order.",
  /** Not an error the customer caused — the content is already theirs, so point at it. */
  [RedeemCodeFailureReason.AlreadyRedeemed]:
    "You've already redeemed that code. The items it unlocked are in your Digital Library.",
  [RedeemCodeFailureReason.LimitExceeded]: "That code has no seats left. Everyone it covers has already claimed one.",
  [RedeemCodeFailureReason.CtgUsed]: "That code has already been used for this study.",
  [RedeemCodeFailureReason.Unknown]:
    "We couldn't redeem that code. Please try again, or contact Lifeway Customer Service.",
}

/**
 * The fallback is load-bearing despite the total `Record` above. The facade
 * folds any code it does not recognise into `UNKNOWN`, but a *deployed* graph
 * would not: an enum member added upstream arrives at runtime in a build whose
 * generated types predate it, and `MESSAGES[reason]` is then `undefined` with
 * nothing having failed to compile.
 */
export const redeemFailureMessage = (reason: RedeemCodeFailureReason): string =>
  MESSAGES[reason] ?? MESSAGES[RedeemCodeFailureReason.Unknown]
